import React, { useEffect, useMemo, useRef, useState } from 'react';
import { getZonasComunes, getReservas, createReserva, cancelReserva, createZonaComun, getComunidades } from '../services/api';
import { useToast } from '../components/Toast';
import DateInput from '../components/DateInput';
import Button from '../components/ui/Button';
import EmptyState from '../components/EmptyState';
import ConfirmModal from '../components/ConfirmModal';
import './Reservas.css';

function Reservas({ user }) {
  const toast = useToast();
  const [zonas, setZonas] = useState([]);
  const [reservas, setReservas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedZona, setSelectedZona] = useState('');
  const [fecha, setFecha] = useState(() => new Date().toISOString().slice(0, 10));
  const [horaInicio, setHoraInicio] = useState('10:00');
  const [horaFin, setHoraFin] = useState('11:00');
  const [creandoZona, setCreandoZona] = useState(false);
  const [zonaNombre, setZonaNombre] = useState('');
  const [zonaDescripcion, setZonaDescripcion] = useState('');
  const [ocupacionMaxima, setOcupacionMaxima] = useState('1');
  const [zonaHoraApertura, setZonaHoraApertura] = useState('08:00');
  const [zonaHoraCierre, setZonaHoraCierre] = useState('22:00');
  const [zonasComunidadId, setZonasComunidadId] = useState('');
  const [comunidades, setComunidades] = useState([]);
  const [comunidadSeleccionada, setComunidadSeleccionada] = useState('');
  const [currentMonth, setCurrentMonth] = useState(() => {
    const today = new Date();
    return new Date(today.getFullYear(), today.getMonth(), 1);
  });
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null });

  const nuevaReservaRef = useRef(null);

  const puedeGestionarZonas = user?.rol === 'administrador' || user?.rol === 'presidente';

  const opcionesHora = useMemo(() => {
    const horas = [];
    for (let h = 0; h < 24; h++) {
      for (let m = 0; m < 60; m += 15) {
        horas.push(`${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`);
      }
    }
    return horas;
  }, []);

  const zonasOrdenadas = useMemo(() => {
    return [...zonas].sort((a, b) => a.nombre.localeCompare(b.nombre));
  }, [zonas]);

  const opcionesHoraReserva = useMemo(() => {
    const zona = zonas.find(z => String(z.id) === String(selectedZona));
    if (!zona || !zona.hora_apertura || !zona.hora_cierre) return opcionesHora;
    const apertura = zona.hora_apertura.slice(0, 5);
    const cierre = zona.hora_cierre.slice(0, 5);
    return opcionesHora.filter(h => h >= apertura && h <= cierre);
  }, [selectedZona, zonas, opcionesHora]);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      setErrorMessage('');
      const filtros = {};
      if (comunidadSeleccionada) {
        filtros.comunidad_id = comunidadSeleccionada;
      }
      const [zonasRes, reservasRes, comunidadesRes] = await Promise.all([
        getZonasComunes(comunidadSeleccionada || null),
        getReservas(filtros),
        getComunidades()
      ]);
      setZonas(zonasRes.data);
      setReservas(reservasRes.data);
      setComunidades(comunidadesRes.data);
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar datos de reservas');
      setErrorMessage('No se pudieron cargar las reservas. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    cargarDatos();
  }, [comunidadSeleccionada]);

  const formatDateKey = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const formatDateLabel = (dateKey) => {
    if (!dateKey) return '';
    const date = new Date(`${dateKey}T00:00:00`);
    if (Number.isNaN(date.getTime())) return dateKey;
    return date.toLocaleDateString('es-ES', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const formatLongDate = (dateKey) => {
    if (!dateKey) return '';
    const date = new Date(`${dateKey}T00:00:00`);
    if (Number.isNaN(date.getTime())) return dateKey;
    return date.toLocaleDateString('es-ES', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const handleCrearReserva = async (e) => {
    e.preventDefault();
    if (!selectedZona || !fecha || !horaInicio || !horaFin) {
      toast.error('Por favor completa todos los campos');
      return;
    }

    try {
      await createReserva({
        zona_id: selectedZona,
        fecha,
        hora_inicio: horaInicio,
        hora_fin: horaFin,
        ...(comunidadSeleccionada && { comunidad_id: comunidadSeleccionada })
      });
      toast.success('Reserva creada correctamente');
      setSuccessMessage(`Reserva confirmada para el ${formatDateLabel(fecha)} a las ${horaInicio}`);
      setTimeout(() => setSuccessMessage(''), 4000);
      setFecha(new Date().toISOString().slice(0, 10));
      setHoraInicio('10:00');
      setHoraFin('11:00');
      setSelectedZona('');
      cargarDatos();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al crear reserva');
    }
  };

  const openConfirm = (title, message, onConfirm) => {
    setConfirmModal({ isOpen: true, title, message, onConfirm });
  };
  const closeConfirm = () => setConfirmModal(prev => ({ ...prev, isOpen: false }));

  const handleCancelarReserva = (id) => {
    openConfirm(
      'Cancelar reserva',
      '¿Estás seguro de que quieres cancelar esta reserva? Esta acción no se puede deshacer.',
      async () => {
        closeConfirm();
        try {
          await cancelReserva(id);
          toast.success('Reserva cancelada');
          cargarDatos();
        } catch (error) {
          toast.error('Error al cancelar reserva');
        }
      }
    );
  };

  const handleCrearZona = async (e) => {
    e.preventDefault();
    if (!zonaNombre) {
      toast.error('El nombre de la zona es requerido');
      return;
    }

    try {
      await createZonaComun({
        nombre: zonaNombre,
        descripcion: zonaDescripcion,
        ocupacion_maxima: parseInt(ocupacionMaxima),
        hora_apertura: zonaHoraApertura,
        hora_cierre: zonaHoraCierre,
        comunidad_id: comunidadSeleccionada || zonasComunidadId || null
      });
      toast.success('Zona creada correctamente');
      setZonaNombre('');
      setZonaDescripcion('');
      setOcupacionMaxima('1');
      setZonaHoraApertura('08:00');
      setZonaHoraCierre('22:00');
      setZonasComunidadId('');
      setCreandoZona(false);
      cargarDatos();
    } catch (error) {
      toast.error('Error al crear zona');
    }
  };

  const todayKey = formatDateKey(new Date());
  const currentMonthLabel = currentMonth.toLocaleDateString('es-ES', {
    month: 'long',
    year: 'numeric'
  });

  const reservasPorFecha = useMemo(() => {
    const map = new Map();
    reservas.forEach((reserva) => {
      const key = reserva.fecha ? reserva.fecha.slice(0, 10) : '';
      if (!key) return;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(reserva);
    });
    return map;
  }, [reservas]);

  const calendarCells = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDay = new Date(year, month, 1);
    const offset = (firstDay.getDay() + 6) % 7;
    const totalDays = new Date(year, month + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < offset; i += 1) {
      cells.push(null);
    }
    for (let day = 1; day <= totalDays; day += 1) {
      const date = new Date(year, month, day);
      cells.push({
        day,
        dateKey: formatDateKey(date)
      });
    }
    return cells;
  }, [currentMonth]);

  const reservasDelMes = useMemo(() => {
    const monthPrefix = `${currentMonth.getFullYear()}-${String(currentMonth.getMonth() + 1).padStart(2, '0')}-`;
    return reservas.filter((reserva) => reserva.fecha && reserva.fecha.startsWith(monthPrefix));
  }, [reservas, currentMonth]);

  const reservasDia = useMemo(() => {
    return reservasPorFecha.get(fecha) || [];
  }, [reservasPorFecha, fecha]);

  const zonaSeleccionadaData = useMemo(() => {
    return zonas.find((zona) => String(zona.id) === String(selectedZona));
  }, [zonas, selectedZona]);

  const reservasDiaZona = useMemo(() => {
    if (!selectedZona) return reservasDia;
    return reservasDia.filter((reserva) => String(reserva.zona_id) === String(selectedZona));
  }, [reservasDia, selectedZona]);

  const handlePrevMonth = () => {
    const next = new Date(currentMonth);
    next.setMonth(next.getMonth() - 1);
    setCurrentMonth(new Date(next.getFullYear(), next.getMonth(), 1));
  };

  const handleNextMonth = () => {
    const next = new Date(currentMonth);
    next.setMonth(next.getMonth() + 1);
    setCurrentMonth(new Date(next.getFullYear(), next.getMonth(), 1));
  };

  return (
    <div className="reservas-page">
      <header className="reservas-header">
        <div>
          <h1 className="reservas-title">📅 Reservas</h1>
          <p className="reservas-subtitle">Reserva zonas comunes de tu comunidad</p>
        </div>
        <div className="reservas-header-actions">
          <div className="reservas-count">{reservas.length} reservas</div>
          {puedeGestionarZonas && (
            <Button
              variant="primary"
              size="md"
              onClick={() => setCreandoZona(true)}
            >
              Crear nueva zona
            </Button>
          )}
          <Button
            variant="secondary"
            size="md"
            onClick={() => {
              setFecha(todayKey);
              setTimeout(() => {
                nuevaReservaRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }, 50);
            }}
          >
            Nueva reserva
          </Button>
        </div>
      </header>

      {errorMessage && (
        <div className="reservas-error">
          {errorMessage}
        </div>
      )}

      {(user?.rol === 'administrador' || user?.rol === 'presidente') && comunidades.length > 0 && (
        <section className="reservas-toolbar">
          <div className="reservas-field">
            <label className="form-label">Comunidad</label>
            <select
              className="form-control reservas-select"
              value={comunidadSeleccionada}
              onChange={(e) => setComunidadSeleccionada(e.target.value)}
            >
              <option value="">Todas las comunidades</option>
              {comunidades.map((comunidad) => (
                <option key={comunidad.id} value={comunidad.id}>{comunidad.nombre}</option>
              ))}
            </select>
          </div>
        </section>
      )}

      {/* Sección "Reservas del día" visible solo en móvil, encima del calendario */}
      <div className="reservas-dia-mobile">
        <div className="reservas-detail-header">
          <div>
            <h2 className="reservas-section-title">Reservas del día</h2>
            <p className="reservas-section-subtitle">{formatLongDate(fecha)}</p>
          </div>
          <div className="reservas-badge">{reservasDia.length} reservas</div>
        </div>
        {reservasDia.length === 0 ? (
          <p className="reservas-muted">No hay reservas para este día.</p>
        ) : (
          <div className="reservas-day-list">
            {reservasDia.map((reserva) => {
              const zona = zonas.find((z) => z.id === reserva.zona_id);
              const esMia = reserva.usuario_id && user?.id && String(reserva.usuario_id) === String(user.id);
              return (
                <div key={reserva.id} className="reservas-day-item">
                  <div>
                    <div className="reservas-day-title">
                      {zona?.nombre || 'Zona común'}
                      {esMia && <span className="reservas-day-pill">Mi reserva</span>}
                    </div>
                    <div className="reservas-day-meta">
                      {reserva.hora_inicio} - {reserva.hora_fin}
                      {reserva.usuario_nombre && <span>• {reserva.usuario_nombre}</span>}
                    </div>
                  </div>
                  {reserva.puede_cancelar && (
                    <Button variant="danger" size="sm" onClick={() => handleCancelarReserva(reserva.id)}>
                      Cancelar
                    </Button>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <section className="reservas-layout">
        <div className="reservas-calendar-card">
          <div className="reservas-calendar-header">
            <div>
              <h2 className="reservas-section-title">Calendario</h2>
              <p className="reservas-section-subtitle">Mes actual: {currentMonthLabel}</p>
            </div>
            <div className="reservas-calendar-nav">
              <button className="reservas-nav-button" onClick={handlePrevMonth}>
                ← Mes anterior
              </button>
              <button className="reservas-nav-button" onClick={handleNextMonth}>
                Mes siguiente →
              </button>
            </div>
          </div>

          <div className="reservas-weekdays">
            {['L', 'M', 'X', 'J', 'V', 'S', 'D'].map((day) => (
              <div key={day} className="reservas-weekday">{day}</div>
            ))}
          </div>

          {loading ? (
            <div className="reservas-calendar-grid reservas-calendar-grid--loading">
              {Array.from({ length: 35 }).map((_, index) => (
                <div key={index} className="reservas-skeleton-cell"></div>
              ))}
            </div>
          ) : (
            <div className="reservas-calendar-grid">
              {calendarCells.map((cell, index) => {
                if (!cell) {
                  return <div key={`empty-${index}`} className="reservas-cell reservas-cell--empty"></div>;
                }

                const dayReservas = reservasPorFecha.get(cell.dateKey) || [];
                const isPast = cell.dateKey < todayKey;
                const isMine = dayReservas.some(
                  (reserva) => reserva.usuario_id && user?.id && String(reserva.usuario_id) === String(user.id)
                );
                const isBusy = dayReservas.length > 0;
                const isSelected = cell.dateKey === fecha;
                const isToday = cell.dateKey === todayKey;
                let status = 'free';
                if (isPast) status = 'past';
                else if (isMine) status = 'mine';
                else if (isBusy) status = 'busy';

                return (
                  <button
                    key={cell.dateKey}
                    className={`reservas-cell reservas-cell--${status} ${isSelected ? 'reservas-cell--selected' : ''} ${isToday ? 'reservas-cell--today' : ''}`}
                    onClick={() => setFecha(cell.dateKey)}
                  >
                    <span className="reservas-day-number">{cell.day}</span>
                    {dayReservas.length > 0 && (
                      <span className="reservas-day-count">{dayReservas.length}</span>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {!loading && reservasDelMes.length === 0 && (
            <EmptyState
              icon="📅"
              title="No hay reservas este mes"
              message="Selecciona un día para crear una reserva"
              size="small"
            />
          )}

          <div className="reservas-legend">
            <div className="reservas-legend-item">
              <span className="reservas-legend-dot reservas-legend-dot--free"></span>
              Disponible
            </div>
            <div className="reservas-legend-item">
              <span className="reservas-legend-dot reservas-legend-dot--busy"></span>
              Ocupado
            </div>
            <div className="reservas-legend-item">
              <span className="reservas-legend-dot reservas-legend-dot--mine"></span>
              Mi reserva
            </div>
            <div className="reservas-legend-item">
              <span className="reservas-legend-dot reservas-legend-dot--past"></span>
              Pasado
            </div>
          </div>
        </div>

        <aside className="reservas-detail-card">
          {puedeGestionarZonas && creandoZona && (
            <div className="reservas-section">
              <form className="reservas-zone-form" onSubmit={handleCrearZona}>
                {puedeGestionarZonas && !comunidadSeleccionada && comunidades.length > 0 && (
                  <div className="reservas-field">
                    <label className="form-label">Comunidad *</label>
                    <select
                      className="form-control reservas-select"
                      value={zonasComunidadId}
                      onChange={(e) => setZonasComunidadId(e.target.value)}
                      required
                    >
                      <option value="">Seleccionar comunidad</option>
                      {comunidades.map((c) => (
                        <option key={c.id} value={c.id}>{c.nombre}</option>
                      ))}
                    </select>
                  </div>
                )}
                <div className="reservas-field">
                  <label className="form-label">Nombre de la zona *</label>
                  <input type="text" className="form-control" placeholder="Ej: Sala de reuniones" value={zonaNombre} onChange={(e) => setZonaNombre(e.target.value)} required />
                </div>
                <div className="reservas-field">
                  <label className="form-label">Ocupacion maxima *</label>
                  <input type="number" className="form-control" placeholder="Ej: 50" value={ocupacionMaxima} onChange={(e) => setOcupacionMaxima(e.target.value)} min="1" />
                </div>
                <div className="reservas-field">
                  <label className="form-label">Hora apertura *</label>
                  <input type="time" className="form-control" value={zonaHoraApertura} onChange={(e) => setZonaHoraApertura(e.target.value)} />
                </div>
                <div className="reservas-field">
                  <label className="form-label">Hora cierre *</label>
                  <input type="time" className="form-control" value={zonaHoraCierre} onChange={(e) => setZonaHoraCierre(e.target.value)} />
                </div>
                <div className="reservas-field">
                  <label className="form-label">Descripcion</label>
                  <textarea className="form-control" placeholder="Describe la zona..." value={zonaDescripcion} onChange={(e) => setZonaDescripcion(e.target.value)} rows="3" />
                </div>
                <div className="reservas-zone-actions">
                  <Button variant="primary" type="submit" size="md">Crear zona común</Button>
                  <Button variant="ghost" size="md" onClick={() => { setCreandoZona(false); setZonaNombre(''); setZonaDescripcion(''); setZonasComunidadId(''); }}>Cancelar</Button>
                </div>
              </form>
            </div>
          )}

          <div className="reservas-detail-header">
            <div>
              <h2 className="reservas-section-title">Detalle del dia</h2>
              <p className="reservas-section-subtitle">{formatLongDate(fecha)}</p>
            </div>
            <div className="reservas-badge">{reservasDia.length} reservas</div>
          </div>

          {successMessage && (
            <div className="reservas-success">
              {successMessage}
            </div>
          )}

          {zonaSeleccionadaData?.normas && (
            <div className="reservas-rules">
              <div className="reservas-rules-title">ℹ️ Normas</div>
              <p>{zonaSeleccionadaData.normas}</p>
            </div>
          )}

          <div className="reservas-section reservas-section--dia-aside">
            <h3 className="reservas-section-heading">Reservas del dia</h3>
            {reservasDia.length === 0 ? (
              <p className="reservas-muted">No hay reservas para este dia.</p>
            ) : (
              <div className="reservas-day-list">
                {reservasDia.map((reserva) => {
                  const zona = zonas.find((zonaItem) => zonaItem.id === reserva.zona_id);
                  const esMia = reserva.usuario_id && user?.id && String(reserva.usuario_id) === String(user.id);
                  return (
                    <div key={reserva.id} className="reservas-day-item">
                      <div>
                        <div className="reservas-day-title">
                          {zona?.nombre || 'Zona comun'}
                          {esMia && <span className="reservas-day-pill">Mi reserva</span>}
                        </div>
                        <div className="reservas-day-meta">
                          {reserva.hora_inicio} - {reserva.hora_fin}
                          {reserva.usuario_nombre && <span>• {reserva.usuario_nombre}</span>}
                        </div>
                      </div>
                      {reserva.puede_cancelar && (
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleCancelarReserva(reserva.id)}
                        >
                          Cancelar
                        </Button>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="reservas-section" ref={nuevaReservaRef}>
            <h3 className="reservas-section-heading">Nueva reserva</h3>
            <form className="reservas-form" onSubmit={handleCrearReserva}>
              <div className="reservas-field">
                <label className="form-label">Zona comun *</label>
                <select
                  className="form-control reservas-select"
                  value={selectedZona}
                  onChange={(e) => setSelectedZona(e.target.value)}
                  required
                >
                  <option value="">Seleccionar zona</option>
                  {zonasOrdenadas.map((zona) => (
                    <option key={zona.id} value={zona.id}>{zona.nombre}</option>
                  ))}
                </select>
              </div>
              <div className="reservas-field">
                <label className="form-label">Fecha *</label>
                <DateInput
                  value={fecha}
                  onChange={setFecha}
                  placeholder="dd-mm-aaaa"
                />
              </div>
              <div className="reservas-field">
                <label className="form-label">Inicio *</label>
                <select
                  className="form-control reservas-select"
                  value={horaInicio}
                  onChange={(e) => setHoraInicio(e.target.value)}
                  required
                >
                  {opcionesHoraReserva.map((hora) => (
                    <option key={hora} value={hora}>{hora}</option>
                  ))}
                </select>
              </div>
              <div className="reservas-field">
                <label className="form-label">Fin *</label>
                <select
                  className="form-control reservas-select"
                  value={horaFin}
                  onChange={(e) => setHoraFin(e.target.value)}
                  required
                >
                  {opcionesHoraReserva.map((hora) => (
                    <option key={hora} value={hora}>{hora}</option>
                  ))}
                </select>
              </div>
              <Button variant="primary" type="submit" size="md">
                Confirmar reserva
              </Button>
            </form>
          </div>

        </aside>
      </section>
      <ConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={closeConfirm}
        confirmLabel="Cancelar reserva"
        variant="danger"
      />
    </div>
  );
}

export default Reservas;
